import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAdminSessions } from '../../api/admin';
import { ChangeTutorModal, canChangeTutor } from './ChangeTutorModal';
import { Page, PageHeader } from '../../components/Layout';
import { FilterBar, FilterSelect, SearchInput } from '../../components/FilterBar';
import { Pagination } from '../../components/Pagination';
import { SessionStatusBadge } from '../../components/Badge';
import { UserCell } from '../../components/Avatar';
import { EmptyState, ErrorState } from '../../components/EmptyState';
import { SkeletonRows } from '../../components/Spinner';
import { useDebounced } from '../../hooks/useDebounced';
import { formatRelative, formatSlotRange } from '../../lib/format';
import { ApiError } from '../../lib/api';
import type { Session, SessionScope, SessionStatus } from '../../types/api';

export function SessionsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  // Deep links from a person's page land here pre-filtered to them.
  const tutorId = params.get('tutorId') ?? undefined;
  const studentId = params.get('studentId') ?? undefined;

  const [search, setSearch] = useState('');
  const [scope, setScope] = useState<SessionScope>(tutorId || studentId ? 'all' : 'upcoming');
  const [status, setStatus] = useState<SessionStatus | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [changingTutorOn, setChangingTutorOn] = useState<Session | null>(null);

  const debouncedSearch = useDebounced(search);

  const query = useMemo(
    () => ({
      page,
      limit,
      scope,
      status: status || undefined,
      q: debouncedSearch.trim() || undefined,
      tutorId,
      studentId,
      from: from ? new Date(from).toISOString() : undefined,
      to: to ? new Date(to).toISOString() : undefined,
    }),
    [page, limit, scope, status, debouncedSearch, tutorId, studentId, from, to],
  );

  const sessions = useAdminSessions(query);
  const personFilter = Boolean(tutorId || studentId);
  const hasFilters = Boolean(debouncedSearch || status || from || to || personFilter);

  const resetFilters = () => {
    setSearch('');
    setStatus('');
    setFrom('');
    setTo('');
    setPage(1);
    if (personFilter) setParams({}, { replace: true });
  };

  return (
    <Page>
      <PageHeader
        title="Lessons"
        description="Every booking on the platform, live and historical."
      />

      {personFilter && (
        <div className="mb-3 flex items-center gap-2 rounded-md border border-accent/30 bg-accent-wash px-3 py-2 text-xs">
          <span className="text-ink-2">Showing lessons for one person only.</span>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setParams({}, { replace: true })}
          >
            Show everyone
          </button>
        </div>
      )}

      <div className="panel overflow-hidden">
        <FilterBar onReset={resetFilters} hasFilters={hasFilters}>
          <SearchInput
            value={search}
            onChange={(v) => {
              setSearch(v);
              setPage(1);
            }}
            placeholder="Search either participant"
          />
          <select
            className="select h-[2.125rem] w-auto"
            value={scope}
            aria-label="Time scope"
            onChange={(e) => {
              setScope(e.target.value as SessionScope);
              setPage(1);
            }}
          >
            <option value="upcoming">Upcoming</option>
            <option value="past">Past</option>
            <option value="all">All time</option>
          </select>
          <FilterSelect
            label="Status"
            value={status}
            onChange={(v) => {
              setStatus(v);
              setPage(1);
            }}
            placeholder="Any status"
            options={[
              { value: 'scheduled', label: 'Scheduled' },
              { value: 'in_progress', label: 'In progress' },
              { value: 'completed', label: 'Completed' },
              { value: 'cancelled', label: 'Cancelled' },
              { value: 'no_show', label: 'No show' },
            ]}
          />
          <label className="flex items-center gap-1.5 text-xs text-ink-3">
            From
            <input
              type="date"
              className="input h-[2.125rem] w-auto py-0"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setPage(1);
              }}
            />
          </label>
          <label className="flex items-center gap-1.5 text-xs text-ink-3">
            To
            <input
              type="date"
              className="input h-[2.125rem] w-auto py-0"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setPage(1);
              }}
            />
          </label>
        </FilterBar>

        {sessions.isError ? (
          <ErrorState
            message={sessions.error instanceof ApiError ? sessions.error.message : undefined}
            onRetry={() => void sessions.refetch()}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Tutor</th>
                    <th>When</th>
                    <th>Subject</th>
                    <th>Status</th>
                    <th>Booked</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {sessions.isLoading ? (
                    <SkeletonRows rows={8} cols={7} />
                  ) : sessions.data && sessions.data.data.length > 0 ? (
                    sessions.data.data.map((s) => (
                      <tr
                        key={s.id}
                        data-clickable="true"
                        onClick={() => navigate(`/sessions/${s.id}`)}
                      >
                        <td>
                          <UserCell
                            name={s.student.fullName}
                            src={s.student.avatarUrl}
                            trailing={
                              s.student.isOnline ? (
                                <span className="pulse-dot" role="img" aria-label="Online" />
                              ) : null
                            }
                          />
                        </td>
                        <td>
                          <UserCell
                            name={s.tutor.fullName}
                            src={s.tutor.avatarUrl}
                            trailing={
                              s.tutor.isOnline ? (
                                <span className="pulse-dot" role="img" aria-label="Online" />
                              ) : null
                            }
                          />
                        </td>
                        <td className="whitespace-nowrap text-xs text-ink-2">
                          {formatSlotRange(s.startTime, s.endTime)}
                        </td>
                        <td className="text-xs text-ink-2">{s.subject ?? '—'}</td>
                        <td>
                          <SessionStatusBadge status={s.status} />
                        </td>
                        <td className="text-xs text-ink-3">
                          <span title={new Date(s.createdAt).toLocaleString()}>
                            {formatRelative(s.createdAt)}
                          </span>
                        </td>
                        <td className="text-right">
                          {canChangeTutor(s) && (
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              // The row itself opens the lesson, so this has
                              // to stop before the row's handler sees it.
                              onClick={(e) => {
                                e.stopPropagation();
                                setChangingTutorOn(s);
                              }}
                            >
                              Change tutor
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="border-b-0 p-0">
                        <EmptyState
                          title={
                            scope === 'upcoming'
                              ? 'Nothing coming up'
                              : 'No lessons match these filters'
                          }
                          description={
                            scope === 'upcoming'
                              ? 'Every booked lesson has already finished. Switch to "All time" to see the history.'
                              : 'Try a wider date range or a different status.'
                          }
                          action={
                            hasFilters ? (
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={resetFilters}
                              >
                                Clear filters
                              </button>
                            ) : null
                          }
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {sessions.data && sessions.data.data.length > 0 && (
              <Pagination
                meta={sessions.data.meta}
                onPageChange={setPage}
                onLimitChange={(l) => {
                  setLimit(l);
                  setPage(1);
                }}
              />
            )}
          </>
        )}
      </div>

      {changingTutorOn && (
        <ChangeTutorModal
          key={changingTutorOn.id}
          session={changingTutorOn}
          onClose={() => setChangingTutorOn(null)}
        />
      )}
    </Page>
  );
}
